<template>
  <div class="max-w-xl mx-auto p-4">
    <form @submit.prevent="handleSubmit" class="space-y-4 bg-white rounded p-4 shadow">
      <div>
        <label class="block text-sm font-medium"
          >Equipment <span class="text-red-500">*</span></label
        >
        <select v-model.number="form.equipment_id" class="w-full p-2 border rounded">
          <option :value="0">Select equipment</option>
          <option v-for="eq in equipmentList" :key="eq.id" :value="eq.id">{{ eq.name }}</option>
        </select>
        <p v-if="errors.equipment_id" class="text-red-500 text-xs">{{ errors.equipment_id }}</p>
      </div>

      <div>
        <label class="block text-sm font-medium"
          >Log Date <span class="text-red-500">*</span></label
        >
        <input type="date" v-model="form.log_date" class="w-full p-2 border rounded" />
        <p v-if="errors.log_date" class="text-red-500 text-xs">{{ errors.log_date }}</p>
      </div>

      <div v-if="showHours">
        <label class="block text-sm font-medium">Hours Used</label>
        <input
          type="number"
          v-model.number="form.hours_used"
          min="0"
          step="0.1"
          class="w-full p-2 border rounded"
        />
        <p v-if="errors.hours_used" class="text-red-500 text-xs">{{ errors.hours_used }}</p>
      </div>

      <div v-if="showFuel">
        <label class="block text-sm font-medium">Fuel Used</label>
        <input
          type="number"
          v-model.number="form.fuel_used"
          min="0"
          step="0.01"
          class="w-full p-2 border rounded"
        />
        <p v-if="errors.fuel_used" class="text-red-500 text-xs">{{ errors.fuel_used }}</p>
      </div>

      <div>
        <label class="block text-sm font-medium">Tasks Performed</label>
        <textarea
          v-model="form.tasks_performed"
          class="w-full p-2 border rounded"
          rows="3"
        ></textarea>
      </div>

      <div>
        <label class="block text-sm font-medium">Notes</label>
        <textarea v-model="form.notes" class="w-full p-2 border rounded" rows="3"></textarea>
      </div>

      <div class="flex justify-end space-x-2">
        <button type="button" @click="$emit('cancel')" class="btn-secondary">Cancel</button>
        <button type="submit" class="btn-primary">{{ submitLabel }}</button>
      </div>
    </form>
  </div>
</template>
<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import type { UsageLogCreate, UsageLog } from '@/types/usage'
import { useEquipmentStore } from '@/stores/equipmentStore'

const props = defineProps<{
  initial?: UsageLog | null
  submitLabel?: string
}>()
const emit = defineEmits<{
  (e: 'save', data: UsageLogCreate): void
  (e: 'cancel'): void
}>()

const equipmentStore = useEquipmentStore()

const equipmentList = computed(() =>
  Array.isArray(equipmentStore.equipment) ? equipmentStore.equipment : [],
)

const form = ref<UsageLogCreate>({
  equipment_id: 0,
  log_date: new Date().toISOString().slice(0, 10),
  hours_used: undefined,
  fuel_used: undefined,
  tasks_performed: undefined,
  notes: undefined,
})

const errors = ref<Record<string, string>>({})

const submitLabel = computed(() => props.submitLabel || (props.initial ? 'Update' : 'Create'))

onMounted(() => {
  if (equipmentList.value.length === 0) equipmentStore.fetchEquipment()
  if (props.initial) populateFromInitial(props.initial)
})

watch(
  () => props.initial,
  (v) => {
    if (v) populateFromInitial(v)
  },
)

function populateFromInitial(init: UsageLog) {
  form.value.equipment_id = init.equipment_id
  form.value.log_date = init.log_date.slice(0, 10)
  form.value.hours_used = init.hours_used
  form.value.fuel_used = init.fuel_used
  form.value.tasks_performed = init.tasks_performed
  form.value.notes = init.notes
}

const selectedEquipment = computed(() =>
  equipmentList.value.find((e) => e.id === Number(form.value.equipment_id)),
)
const showFuel = computed(() => !!selectedEquipment.value?.requires_fuel)
const showHours = computed(() => true)

function validate(): boolean {
  errors.value = {}
  let ok = true
  if (!form.value.equipment_id || form.value.equipment_id === 0) {
    errors.value.equipment_id = 'Please select equipment'
    ok = false
  }
  const today = new Date().toISOString().slice(0, 10)
  if (!form.value.log_date) {
    errors.value.log_date = 'Log date is required'
    ok = false
  } else if (form.value.log_date > today) {
    errors.value.log_date = 'Log date cannot be in the future'
    ok = false
  }
  if (form.value.hours_used !== undefined && form.value.hours_used <= 0) {
    errors.value.hours_used = 'Hours used must be greater than 0'
    ok = false
  }
  if (showFuel.value && (form.value.fuel_used === undefined || form.value.fuel_used <= 0)) {
    errors.value.fuel_used = 'Fuel used must be greater than 0'
    ok = false
  }
  return ok
}

async function handleSubmit() {
  if (!validate()) return
  try {
    // send date as YYYY-MM-DD (no time) to avoid backend date-parsing/timezone issues
    const payload: UsageLogCreate = {
      equipment_id: form.value.equipment_id,
      log_date: form.value.log_date, // already YYYY-MM-DD from input
      hours_used: form.value.hours_used,
      fuel_used: form.value.fuel_used,
      tasks_performed: form.value.tasks_performed,
      notes: form.value.notes,
    }
    emit('save', payload)
  } catch (err: unknown) {
    // map API validation errors if present
    if (err && typeof err === 'object' && 'response' in err) {
      // axios-style error
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e: any = err as any
      if (e.response && e.response.data) {
        const data = e.response.data
        for (const k in data) {
          errors.value[k] = Array.isArray(data[k]) ? String(data[k][0]) : String(data[k])
        }
      }
    }
  }
}
</script>
